import React, { createContext, useContext, useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';

import { formatPrice } from '../utils/format';
import { useAuth } from './auth';

type CartContextProps = {
	children: React.ReactNode;
}
type ItemData = {
	id: string;
	categoria: string;
	cod_barras: string;
	cod_pro: string;
	comiss_ind: number;
	comiss_ven: number;
	complementos: ComplementoData[];
	descricao: string;
	nome: string;
	obs: string;
	promocao: string;
	qtd: number;
	url_imagem: string;
	valor: number;
	valorFormatted: string;
	total: number;
	totalFormatted: string;
}
type ComplementoData = {
	codigo: number;
	grupo: number;
	nome: string;
	descricao: string;
	custo: number;
	valor: number;
	priceFormatted: string;
	qtd_maxima: number;
	qtd_gratis: number;
	qtd: number;
}
type OperacaoData = {
	id: number;
	nome: string;
	operacao: string;
}
type ClienteData = {
	id: string;
	nome: string;
}
type CartData = {
	id_order: number;
	cliente: ClienteData;
	consumidor: boolean;
	operacao: OperacaoData;
	items: ItemData[];
	merchant_id: string;
	data_cart: string;
	subTotal: number;
	subTotalFormatted: string;
	total: number;
	totalFormatted: string;
}
interface CartState {
	cart: CartData;
}
interface CartContextData {
	cart: CartData;
	initCart(): void;
	resumeCart(order: CartData): void;
	addCart(item: ItemData, incluir: boolean): void;
	removeCart(id: string): void;
	addCartMerchant(merchant_id: string): void;
}

const CartContext = createContext<CartContextData>({} as CartContextData);

const CartProvider: React.FC<CartContextProps> = ({ children }) => {
	const { company } = useAuth();

	const [cart, setCart] = useState<CartData>({
		id_order: 0,
		cliente: { id: '0', nome: 'Consumidor' },
		consumidor: true,
		operacao: { id: 3, nome: 'Consumo Produção', operacao: "saida" },
		items: [],
		merchant_id: '0', //company.id ? company.id : 0,
		data_cart: '',
		subTotal: 0,
		subTotalFormatted: '',
		total: 0,
		totalFormatted: '',
	});

	const [data, setData] = useState<CartState>(() => {
		const cartString = localStorage.getItem('@PortalRazio:cart');

		if (cartString) {
			setCart(JSON.parse(cartString));

			return {
				cart: JSON.parse(cartString)
			};
		}

		return {} as CartState;
	});

	const initCart = useCallback(() => {
		const init = {
			id_order: 0,
			cliente: { id: '0', nome: 'Consumidor' },
			consumidor: true,
			operacao: { id: 3, nome: 'Consumo Produção', operacao: "saida" },
			items: [],
			merchant_id: company.id,
			data_cart: '',
			subTotal: 0,
			subTotalFormatted: '',
			total: 0,
			totalFormatted: '',
		};

		setCart(init);
		setData({ cart: init });

		localStorage.setItem('@PortalRazio:cart', JSON.stringify(init));
	}, []);

	const resumeCart = useCallback((order: CartData) => {
		setCart(order);
		setData({
			cart: order,
		});

		localStorage.setItem('@PortalRazio:cart', JSON.stringify(order));
	}, []);

	const addCart = useCallback(
		(product: ItemData, incluir: boolean) => {
			if (!cart) {
				initCart();
			}

			const {
				id,
				categoria,
				qtd,
				cod_pro,
				comiss_ind,
				comiss_ven,
				cod_barras,
				nome,
				descricao,
				obs,
				promocao,
				url_imagem,
				complementos,
				valor,
				valorFormatted,
				total,
				totalFormatted,
			} = product;

			if (incluir) {
				const uid = uuid();

				const item: ItemData = {
					id: uid,
					qtd,
					categoria,
					cod_pro,
					comiss_ind,
					comiss_ven,
					cod_barras,
					nome,
					descricao,
					obs,
					promocao,
					url_imagem,
					complementos,
					valor,
					valorFormatted,
					total,
					totalFormatted,
				};

				const auxCart = cart;

				// se já existir algum item no carrinho
				if (auxCart.items?.length > 0) {
					auxCart.subTotal += total;
					auxCart.total += total; // auxCart.subTotal;
				} else {
					// se for o primeiro item do carrinho seta a data do carrinho;
					auxCart.data_cart = format(new Date(), 'Pp'); //String(new Date());

					auxCart.subTotal += total;
					auxCart.total += total; // auxCart.subTotal;
				}

				auxCart.subTotal = parseFloat(auxCart.subTotal.toFixed(2));
				auxCart.total = parseFloat(auxCart.total.toFixed(2));

				auxCart.subTotalFormatted = formatPrice(auxCart.subTotal);
				auxCart.totalFormatted = formatPrice(auxCart.total);

				auxCart.items?.push(item);

				setCart(auxCart);
				setData({
					cart: auxCart
				});

				localStorage.setItem('@PortalRazio:cart', JSON.stringify(auxCart));
			} else {
				const auxCart = cart;
				const indexItem = auxCart.items.findIndex(item => item.id === id);

				auxCart.items[indexItem] = product;
				let auxTotal = 0;

				auxCart.items.map(item => {
					auxTotal += item.total;

					return item;
				});

				auxCart.subTotal = parseFloat(auxTotal.toFixed(2));
				auxCart.total = parseFloat(auxTotal.toFixed(2));

				auxCart.subTotalFormatted = formatPrice(auxCart.subTotal);
				auxCart.totalFormatted = formatPrice(auxCart.total);

				setCart(auxCart);
				setData({
					cart: auxCart
				});

				localStorage.setItem('@PortalRazio:cart', JSON.stringify(auxCart));
			}
	}, [cart]);

	const removeCart = useCallback(
		(id: string) => {
			const auxCart = cart;
			const indexItem = auxCart.items.findIndex(item => item.id === id);
			const itemExclude = auxCart.items[indexItem].total;
			auxCart.items.splice(indexItem, 1);

			// if (auxCart.cupom.id_desconto) {}

			if (auxCart.items.length === 0) {
				auxCart.subTotal = 0;
				auxCart.total = 0;

				auxCart.subTotalFormatted = formatPrice(auxCart.subTotal);
				auxCart.totalFormatted = formatPrice(auxCart.total);

				auxCart.data_cart = '';
			} else {
				auxCart.subTotal -= itemExclude;
				auxCart.total = auxCart.subTotal;

				auxCart.subTotal = parseFloat(auxCart.subTotal.toFixed(2));
				auxCart.total = parseFloat(auxCart.total.toFixed(2));

				auxCart.subTotalFormatted = formatPrice(auxCart.subTotal);
				auxCart.totalFormatted = formatPrice(auxCart.total);
			}

			setCart(auxCart);

			setData({
				cart: auxCart
			});

			localStorage.setItem('@PortalRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartMerchant = useCallback((merchant_id: string) => {
		const auxCart = { ...cart, merchant_id };

		setCart({ ...cart, merchant_id });
		setData({ cart: auxCart	});

		localStorage.setItem('@PortalRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	return (
		<CartContext.Provider
			value={{
				cart: data.cart,
				initCart,
				resumeCart,
				addCart,
				removeCart,
				addCartMerchant,
			}}
		>
			{children}
		</CartContext.Provider>
	);
};

function useCart(): CartContextData {
	const context = useContext(CartContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}

export { CartProvider, useCart };